<template>
  <section class="wa-bulk-edit-modal-host">
    <Modal
      width="medium"
      :texts="modalTexts"
      :isConfirmDisabled="!canSubmit || isSubmitting"
      @close="$emit('close')"
      @submit="submit"
    >
      <div class="modern-form wa-bulk-edit">
        <div class="wa-bulk-summary">
          <div class="wa-bulk-summary__text">
            <span class="wa-bulk-summary__title">{{ $t('wa_bulk_edit') }}</span>
            <span class="wa-bulk-summary__hint">{{ $t('wa_bulk_edit_hint', { count: selectedCount }) }}</span>
          </div>
          <span class="wa-bulk-summary__badge">{{ $t('n_selected', { count: selectedCount }) }}</span>
        </div>

        <div class="form-section">
          <h3 class="section-title">{{ $t('wa_bulk_action') }}</h3>
          <div class="wa-bulk-actions" role="radiogroup">
            <button
              v-for="option in actionOptions"
              :key="option.value"
              type="button"
              class="wa-bulk-action-card"
              :class="{ active: action === option.value }"
              role="radio"
              :aria-checked="action === option.value"
              @click="action = option.value"
            >
              <span class="wa-bulk-action-card__radio" aria-hidden="true" />
              <span class="wa-bulk-action-card__body">
                <span class="wa-bulk-action-card__label">{{ option.label }}</span>
                <span class="wa-bulk-action-card__desc">{{ option.description }}</span>
              </span>
            </button>
          </div>
        </div>

        <div class="form-section">
          <h3 class="section-title">{{ actionSectionTitle }}</h3>

          <div v-if="action === 'set_counter'" class="form-grid">
            <div class="form-field">
              <label class="field-label">{{ $t('wa_bulk_counter_state') }}</label>
              <div class="wa-bulk-state-row" role="radiogroup">
                <button
                  v-for="option in counterStateOptions"
                  :key="option.value"
                  type="button"
                  class="wa-bulk-state-chip"
                  :class="{ active: counterState === option.value, danger: option.value === 'off' && counterState === 'off' }"
                  role="radio"
                  :aria-checked="counterState === option.value"
                  @click="counterState = option.value"
                >
                  {{ option.label }}
                </button>
              </div>
              <p class="wa-bulk-field-hint">{{ counterStateHint }}</p>
            </div>
            <div v-if="counterState !== 'off'" class="form-field">
              <label class="field-label">{{ $t('amount_limit') }}</label>
              <input
                v-model.number="amountLimit"
                type="number"
                min="1"
                class="modern-input"
                :placeholder="$t('amount_limit')"
              />
            </div>
          </div>

          <div v-else class="wa-bulk-task-panel">
            <div class="form-field">
              <label class="field-label">{{ $t('search') }}</label>
              <input
                v-model="taskSearch"
                type="search"
                class="modern-input"
                :placeholder="$t('wa_bulk_search_task')"
                autocomplete="off"
              />
            </div>

            <div v-if="isLoadingTasks" class="wa-bulk-empty">{{ $t('loading') }}</div>
            <div v-else-if="!filteredTasks.length" class="wa-bulk-empty">{{ $t('no_results_found') }}</div>
            <ul v-else class="wa-bulk-task-list" role="listbox">
              <li
                v-for="task in filteredTasks"
                :key="task.id"
                class="wa-bulk-task"
                :class="{ selected: isTaskSelected(task.id) }"
                role="option"
                :aria-selected="isTaskSelected(task.id)"
                @click="selectedTaskId = task.id"
              >
                <span class="wa-bulk-task__mark" aria-hidden="true">
                  <span v-if="isTaskSelected(task.id)">✓</span>
                </span>
                <span class="wa-bulk-task__name">{{ task.name || `#${task.id}` }}</span>
              </li>
            </ul>

            <p v-if="selectedTask" class="wa-bulk-selected-task">
              {{ $t('wa_bulk_selected_task') }}:
              <strong>{{ selectedTask.name || `#${selectedTask.id}` }}</strong>
            </p>
            <p v-if="selectedTask && isLoadingCoverage" class="wa-bulk-coverage wa-bulk-coverage--muted">
              {{ $t('loading') }}
            </p>
            <p v-else-if="selectedTask && taskCoverage" class="wa-bulk-coverage">
              {{ taskCoverageText }}
            </p>
          </div>
        </div>
      </div>
    </Modal>
  </section>
</template>

<script>
import Modal from '@/modules/common/components/Modal'
import workingAreaService from '../services/workingAreaService'

export default {
  props: {
    selectedIds: {
      type: Array,
      required: true,
    },
    siteId: {
      type: [Number, String],
      required: true,
    },
  },
  data() {
    return {
      action: 'set_counter',
      amountLimit: 200,
      counterState: 'on',
      taskSearch: '',
      tasks: [],
      selectedTaskId: null,
      isLoadingTasks: false,
      isSubmitting: false,
      taskCoverage: null,
      isLoadingCoverage: false,
      coverageRequestId: 0,
    }
  },
  computed: {
    selectedCount() {
      return (this.selectedIds || []).length
    },
    modalTexts() {
      return {
        headerPrimary: this.$t('wa_bulk_edit'),
        headerSecondary: this.$t('n_selected', { count: this.selectedCount }),
        footerConfirm: this.$t('wa_bulk_apply_to_n', { count: this.selectedCount }),
      }
    },
    actionOptions() {
      return [
        {
          value: 'set_counter',
          label: this.$t('wa_bulk_set_counter'),
          description: this.$t('wa_bulk_set_counter_desc'),
        },
        {
          value: 'add_notification_task',
          label: this.$t('wa_bulk_add_task'),
          description: this.$t('wa_bulk_add_task_desc'),
        },
        {
          value: 'remove_notification_task',
          label: this.$t('wa_bulk_remove_task'),
          description: this.$t('wa_bulk_remove_task_desc'),
        },
      ]
    },
    counterStateOptions() {
      return [
        { value: 'on', label: this.$t('wa_bulk_counter_on') },
        { value: 'off', label: this.$t('wa_bulk_counter_off') },
        { value: 'unchanged', label: this.$t('wa_bulk_counter_unchanged') },
      ]
    },
    counterStateHint() {
      if (this.counterState === 'on') return this.$t('wa_bulk_counter_on_hint')
      if (this.counterState === 'off') return this.$t('wa_bulk_counter_off_hint')
      return this.$t('wa_bulk_counter_unchanged_hint')
    },
    actionSectionTitle() {
      if (this.action === 'set_counter') return this.$t('wa_bulk_set_counter')
      if (this.action === 'add_notification_task') return this.$t('wa_bulk_add_task')
      return this.$t('wa_bulk_remove_task')
    },
    filteredTasks() {
      const query = String(this.taskSearch || '').trim().toLowerCase()
      if (!query) return this.tasks
      return this.tasks.filter((task) => {
        const name = String(task.name || '').toLowerCase()
        const id = String(task.id || '')
        return name.includes(query) || id.includes(query)
      })
    },
    selectedTask() {
      if (this.selectedTaskId == null) return null
      return this.tasks.find((task) => String(task.id) === String(this.selectedTaskId)) || null
    },
    taskCoverageText() {
      if (!this.taskCoverage) return ''
      const withTask = this.taskCoverage.withTask || 0
      const total = this.taskCoverage.total || 0
      const willChange = this.action === 'remove_notification_task'
        ? withTask
        : (this.taskCoverage.withoutTask || 0)
      if (this.action === 'remove_notification_task') {
        return this.$t('wa_bulk_task_coverage_remove', { withTask, total, willChange })
      }
      return this.$t('wa_bulk_task_coverage_add', { withTask, total, willChange })
    },
    canSubmit() {
      if (!this.selectedCount) return false
      if (this.action === 'set_counter') {
        if (this.counterState === 'off') return true
        return Number.isFinite(Number(this.amountLimit)) && Number(this.amountLimit) > 0
      }
      return !!this.selectedTask
    },
  },
  watch: {
    action(next) {
      if (next !== 'set_counter' && !this.tasks.length) {
        this.loadTasks()
      }
      this.selectedTaskId = null
      this.taskSearch = ''
      this.taskCoverage = null
    },
    selectedTaskId() {
      this.loadTaskCoverage()
    },
  },
  methods: {
    isTaskSelected(id) {
      return this.selectedTaskId != null && String(this.selectedTaskId) === String(id)
    },
    async loadTaskCoverage() {
      this.taskCoverage = null
      if (
        this.selectedTaskId == null ||
        !this.siteId ||
        !this.selectedIds.length ||
        (this.action !== 'add_notification_task' && this.action !== 'remove_notification_task')
      ) {
        this.isLoadingCoverage = false
        return
      }
      const requestId = ++this.coverageRequestId
      this.isLoadingCoverage = true
      try {
        const result = await workingAreaService.getBulkNotificationTaskCoverage({
          siteId: this.siteId,
          workingAreaIds: this.selectedIds,
          taskId: this.selectedTaskId,
        })
        if (requestId !== this.coverageRequestId) return
        this.taskCoverage = result
      } catch (err) {
        if (requestId !== this.coverageRequestId) return
        this.taskCoverage = null
      } finally {
        if (requestId === this.coverageRequestId) this.isLoadingCoverage = false
      }
    },
    async loadTasks() {
      if (!this.siteId) return
      this.isLoadingTasks = true
      try {
        const siteServiks = await workingAreaService.getServiksBySiteId(this.siteId)
        const defaults = [
          ...workingAreaService.defaultSelectedWorkingAreaServiks(),
          ...workingAreaService.getDefaultToSelectWorkingAreaServiks(),
        ]
        const combined = [...(siteServiks || []), ...defaults]
        const byId = new Map()
        combined.forEach((item) => {
          if (!item || item.id == null) return
          if (!byId.has(String(item.id))) byId.set(String(item.id), item)
        })
        this.tasks = Array.from(byId.values()).sort((a, b) =>
          String(a.name || '').localeCompare(String(b.name || ''))
        )
      } catch (err) {
        this.tasks = workingAreaService.defaultSelectedWorkingAreaServiks()
      } finally {
        this.isLoadingTasks = false
      }
    },
    async submit() {
      if (!this.canSubmit || this.isSubmitting) return
      this.isSubmitting = true
      try {
        let payload = {}
        if (this.action === 'set_counter') {
          payload = {
            counterState: this.counterState,
          }
          if (this.counterState !== 'off') {
            payload.amountLimit = Number(this.amountLimit)
          }
        } else if (this.action === 'add_notification_task') {
          payload = {
            task: { id: this.selectedTask.id, name: this.selectedTask.name || '' },
          }
        } else {
          payload = { taskId: this.selectedTask.id }
        }

        const result = await workingAreaService.bulkUpdate({
          siteId: this.siteId,
          workingAreaIds: this.selectedIds,
          action: this.action,
          payload,
        })
        this.$emit('applied', result)
      } catch (err) {
        this.$emit('error', err)
      } finally {
        this.isSubmitting = false
      }
    },
  },
  components: { Modal },
}
</script>

<style lang="scss" scoped>
@use "@/styles/mixins" as *;

$primary: #007bff;
$primary-hover: #0056b3;
$background: #ffffff;
$surface: #f8f9fa;
$border: #e0e0e0;
$border-light: #f0f0f0;
$text-primary: #212529;
$text-secondary: #6c757d;
$shadow-sm: 0 1px 3px rgba(0, 0, 0, 0.05);
$radius: 8px;
$radius-sm: 6px;

.modern-form {
  display: flex;
  flex-direction: column;
  gap: 16px;
  padding: 0 24px 8px;
}

.wa-bulk-summary {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 16px;
  padding: 14px 16px;
  border: 1px solid #d6e4f7;
  border-radius: $radius;
  background: linear-gradient(180deg, #f7faff 0%, #f3f7fd 100%);
}

.wa-bulk-summary__text {
  display: flex;
  flex-direction: column;
  gap: 4px;
  min-width: 0;
}

.wa-bulk-summary__title {
  font-size: 15px;
  font-weight: 600;
  color: $text-primary;
  letter-spacing: -0.01em;
}

.wa-bulk-summary__hint {
  font-size: 13px;
  color: $text-secondary;
  line-height: 1.4;
}

.wa-bulk-summary__badge {
  flex-shrink: 0;
  padding: 4px 10px;
  border-radius: 999px;
  border: 1px solid #b8d0f0;
  background: #fff;
  color: #2f5ea8;
  font-size: 12px;
  font-weight: 700;
  white-space: nowrap;
}

.form-section {
  background: $background;
  border-radius: $radius;
  padding: 16px 18px 18px;
  box-shadow: $shadow-sm;
  border: 1px solid $border-light;
}

.section-title {
  font-size: 14px;
  font-weight: 600;
  color: $text-primary;
  margin: 0 0 14px;
  padding-bottom: 10px;
  border-bottom: 2px solid $border-light;
  letter-spacing: -0.01em;
}

.form-grid {
  display: grid;
  grid-template-columns: 1fr;
  gap: 16px;
}

.form-field {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.field-label {
  font-size: 13px;
  font-weight: 500;
  color: $text-primary;
  margin: 0;
  letter-spacing: 0.01em;
}

.modern-input {
  width: 100%;
  box-sizing: border-box;
  padding: 10px 14px;
  font-size: 14px;
  border: 1.5px solid $border;
  border-radius: $radius-sm;
  background: $background;
  color: $text-primary;
  transition: all 0.2s ease;

  &:focus {
    outline: none;
    border-color: $primary;
    box-shadow: 0 0 0 3px rgba(0, 123, 255, 0.1);
  }

  &::placeholder {
    color: $text-secondary;
  }
}

.wa-bulk-actions {
  display: grid;
  grid-template-columns: 1fr;
  gap: 10px;
}

.wa-bulk-action-card {
  display: flex;
  align-items: flex-start;
  gap: 12px;
  width: 100%;
  padding: 12px 14px;
  border: 1.5px solid $border;
  border-radius: $radius;
  background: $background;
  text-align: start;
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    border-color: darken($border, 8%);
    background: $surface;
  }

  &.active {
    border-color: $primary;
    background: rgba(0, 123, 255, 0.06);
    box-shadow: 0 0 0 3px rgba(0, 123, 255, 0.08);
  }
}

.wa-bulk-action-card__radio {
  flex-shrink: 0;
  width: 18px;
  height: 18px;
  margin-top: 2px;
  border: 2px solid #b8c2cc;
  border-radius: 50%;
  background: #fff;
  position: relative;
  transition: all 0.2s ease;

  .wa-bulk-action-card.active & {
    border-color: $primary;

    &::after {
      content: '';
      position: absolute;
      inset: 3px;
      border-radius: 50%;
      background: $primary;
    }
  }
}

.wa-bulk-action-card__body {
  display: flex;
  flex-direction: column;
  gap: 2px;
  min-width: 0;
}

.wa-bulk-action-card__label {
  font-size: 14px;
  font-weight: 600;
  color: $text-primary;
}

.wa-bulk-action-card__desc {
  font-size: 12px;
  color: $text-secondary;
  line-height: 1.35;
}

.wa-bulk-state-row {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
}

.wa-bulk-state-chip {
  padding: 8px 12px;
  border: 1.5px solid $border;
  border-radius: 999px;
  background: $background;
  color: $text-primary;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    border-color: darken($border, 8%);
    background: $surface;
  }

  &.active {
    border-color: $primary;
    background: rgba(0, 123, 255, 0.08);
    color: $primary;
    box-shadow: 0 0 0 3px rgba(0, 123, 255, 0.08);
  }

  &.active.danger {
    border-color: #dc3545;
    background: rgba(220, 53, 69, 0.08);
    color: #c82333;
    box-shadow: 0 0 0 3px rgba(220, 53, 69, 0.08);
  }
}

.wa-bulk-field-hint {
  margin: 0;
  font-size: 12px;
  color: $text-secondary;
  line-height: 1.35;
}

.wa-bulk-task-panel {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.wa-bulk-empty {
  padding: 18px 12px;
  text-align: center;
  font-size: 13px;
  color: $text-secondary;
  border: 1px dashed $border;
  border-radius: $radius-sm;
  background: $surface;
}

.wa-bulk-task-list {
  list-style: none;
  margin: 0;
  padding: 0;
  max-height: 240px;
  overflow: auto;
  border: 1.5px solid $border;
  border-radius: $radius;
  background: $background;
}

.wa-bulk-task {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 10px 12px;
  cursor: pointer;
  border-bottom: 1px solid $border-light;
  transition: background 0.15s ease;

  &:last-child {
    border-bottom: none;
  }

  &:hover {
    background: $surface;
  }

  &.selected {
    background: rgba(0, 123, 255, 0.08);

    .wa-bulk-task__name {
      color: $primary;
      font-weight: 600;
    }

    .wa-bulk-task__mark {
      border-color: $primary;
      background: $primary;
      color: #fff;
    }
  }
}

.wa-bulk-task__mark {
  flex-shrink: 0;
  width: 18px;
  height: 18px;
  border: 1.5px solid #c5ced8;
  border-radius: 4px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  font-size: 11px;
  font-weight: 700;
  color: transparent;
  background: #fff;
}

.wa-bulk-task__name {
  flex: 1;
  min-width: 0;
  font-size: 13px;
  color: $text-primary;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.wa-bulk-selected-task {
  margin: 0;
  font-size: 12px;
  color: $text-secondary;

  strong {
    color: $text-primary;
    font-weight: 600;
  }
}

.wa-bulk-coverage {
  margin: 0;
  padding: 10px 12px;
  border-radius: $radius-sm;
  border: 1px solid #d6e4f7;
  background: #f7faff;
  font-size: 12px;
  font-weight: 600;
  color: #2f5ea8;
  line-height: 1.4;

  &--muted {
    border-color: $border;
    background: $surface;
    color: $text-secondary;
    font-weight: 500;
  }
}

@media (max-width: 768px) {
  .modern-form {
    padding: 0 12px 8px;
  }

  .wa-bulk-summary {
    flex-direction: column;
    align-items: stretch;
  }
}
</style>
